import { useNavigate } from "react-router-dom";

import { useLoadMoreOnVisible } from "../features/catalog/useLoadMoreOnVisible";
import { OrderStatusBadge } from "../features/orders/OrderStatusBadge";
import { useMyOrders } from "../features/orders/useMyOrders";
import { formatDate } from "../lib/date";
import { messages } from "../lib/messages";

/**
 * OrdersPage — «Мои заказы» (docs/07-miniapp-ux.md §2.7): список заказов
 * покупателя. Данные в строке идут одним смыслом: что за заказ → когда →
 * на сколько → что с ним, поэтому статус стоит отдельной строкой и не читается
 * вместе с суммой. Клик по строке открывает карточку заказа.
 */
export function OrdersPage() {
  const navigate = useNavigate();
  const orders = useMyOrders();
  const loadMoreRef = useLoadMoreOnVisible(
    () => {
      if (!orders.isFetchingNextPage) {
        void orders.fetchNextPage();
      }
    },
    Boolean(orders.hasNextPage),
  );

  if (orders.isPending) {
    return <OrdersSkeleton />;
  }

  if (orders.isError) {
    return <p className="p-4 text-center text-hint">{messages.requestFailed}</p>;
  }

  const items = orders.data.items;

  return (
    <div className="min-h-screen bg-bg pb-6">
      <header className="p-4">
        <h1 className="text-lg font-semibold text-text">{messages.myOrdersTitle}</h1>
      </header>

      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
          <p className="text-hint">{messages.myOrdersEmpty}</p>
          <button
            type="button"
            onClick={() => navigate("..")}
            className="rounded-card bg-button px-4 py-2 text-button-text"
          >
            {messages.backToCatalog}
          </button>
        </div>
      ) : (
        <ul className="flex flex-col gap-2 px-4">
          {items.map((order) => (
            <li key={order.id}>
              <button
                type="button"
                onClick={() => navigate(order.id)}
                className="flex w-full flex-col items-start gap-1 rounded-card bg-surface p-3 text-left"
              >
                <span className="font-medium text-text">
                  {messages.orderNumberLabel}
                  {order.number}
                </span>
                <span className="text-sm text-hint">{formatDate(order.created_at)}</span>
                <span className="text-text">{order.total_text}</span>
                <OrderStatusBadge status={order.status} label={order.status_text} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div ref={loadMoreRef} aria-hidden="true" className="h-1" />
    </div>
  );
}

function OrdersSkeleton() {
  return (
    <div className="flex min-h-screen flex-col gap-3 bg-bg p-4">
      <div className="h-16 animate-pulse rounded-card bg-surface" />
      <div className="h-16 animate-pulse rounded-card bg-surface" />
    </div>
  );
}
